import React from 'react';
import { css } from '@emotion/react';
import { Drawer, IconButton, LinearProgress, Link, MenuItem } from '@material-ui/core';
import { ArrowUpward, Close, MailOutline } from '@material-ui/icons';

import CSVDropzone from '@/components/CSVDropzone';
import { PrimaryButton } from '@/styles/blocks';
import { colors } from '@/styles';

import {
    BrowsePill,
    CompactField,
    Divider,
    DropzoneLabel,
    DropzoneWrap,
    Emphasis,
    FieldRow,
    HeaderRow,
    InfoBanner,
    Panel,
    Section,
    SectionNote,
    SectionTitle,
    Subtitle,
    Title,
    DRAWER_WIDTH,
} from './blocks';
import { DEDUPE_NOTE_EMPHASIS, DEDUPE_NOTE_LEAD, DEDUPE_NOTE_TAIL, LEG_OPTIONS } from './constants';
import useAddCarriers from './hooks';
import UploadExceptionsModal from './UploadExceptionsModal';
import { buildCarrierCsvSample } from './utils';

const downloadSample = () => {
    const blob = new Blob([buildCarrierCsvSample()], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'onward_carriers_sample.csv';
    anchor.click();
    URL.revokeObjectURL(url);
};

const CsvUploadSection = ({ state, callbacks }) => {
    const { busy, file, parseError, dropzoneKey, csvLeg } = state;

    return (
        <Section>
            <div>
                <SectionTitle>Method A · CSV Upload</SectionTitle>
                <SectionNote
                    css={css`
                        display: block;
                        margin-top: 8px;
                    `}
                >
                    Format: name, alias, DOT, invite email, address, city, 2 letter state, zip.{' '}
                    <Link component="button" type="button" onClick={downloadSample}>
                        Download a sample here
                    </Link>
                </SectionNote>
            </div>

            <DropzoneWrap>
                <CSVDropzone
                    key={dropzoneKey}
                    accept={{ 'text/csv': ['.csv'] }}
                    disabled={busy}
                    disableChanges={busy}
                    handleFileAdd={callbacks.handleFileAdd}
                    icon={
                        <ArrowUpward
                            css={css`
                                color: ${colors.greys[1]};
                            `}
                        />
                    }
                    label={
                        <DropzoneLabel>
                            {file ? file.name : 'Drag files here, or '}
                            {file ? null : <BrowsePill>Browse files</BrowsePill>}
                        </DropzoneLabel>
                    }
                    fileTypeLabel="Only .CSV files are accepted"
                />
            </DropzoneWrap>

            {parseError ? (
                <SectionNote
                    css={css`
                        color: ${colors.reds.primary};
                    `}
                >
                    {parseError}
                </SectionNote>
            ) : null}

            <FieldRow>
                <CompactField
                    select
                    variant="outlined"
                    value={csvLeg}
                    onChange={callbacks.setCsvLeg}
                    disabled={busy}
                    css={css`
                        flex: 1 1 auto;
                    `}
                >
                    {LEG_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </CompactField>
                <PrimaryButton
                    onClick={callbacks.parseAndReview}
                    disabled={!file || busy}
                    loading={busy}
                    css={css`
                        flex: 0 0 auto;
                        min-width: 0;
                        font-size: 12px;
                        text-transform: uppercase;
                        white-space: nowrap;
                    `}
                >
                    Parse &amp; Review
                </PrimaryButton>
            </FieldRow>

            <SectionNote>Applied to every row in the file. Upload once per leg to add a mix.</SectionNote>
        </Section>
    );
};

const InviteOneSection = ({ state, callbacks }) => {
    const { busy, manualForm, manualValid, unknownAirportCodes } = state;

    return (
        <Section>
            <div>
                <SectionTitle>Method B · Invite one by one</SectionTitle>
                <SectionNote
                    css={css`
                        display: block;
                        margin-top: 8px;
                    `}
                >
                    We&apos;ll email them an invite to finish signup.
                </SectionNote>
            </div>

            <FieldRow>
                <CompactField
                    variant="outlined"
                    placeholder="Legal name"
                    value={manualForm.business_name}
                    onChange={callbacks.setManualField('business_name')}
                    disabled={busy}
                    css={css`
                        flex: 1 1 auto;
                    `}
                />
                <CompactField
                    variant="outlined"
                    placeholder="DOT number"
                    value={manualForm.dot_number}
                    onChange={callbacks.setManualField('dot_number')}
                    disabled={busy}
                    css={css`
                        flex: 0 0 140px;
                    `}
                />
            </FieldRow>

            <FieldRow>
                <CompactField
                    variant="outlined"
                    placeholder="Alias (optional)"
                    value={manualForm.alias}
                    onChange={callbacks.setManualField('alias')}
                    disabled={busy}
                    css={css`
                        flex: 1 1 auto;
                    `}
                />
            </FieldRow>

            <FieldRow>
                <CompactField
                    variant="outlined"
                    type="email"
                    placeholder="Invite email"
                    value={manualForm.email}
                    onChange={callbacks.setManualField('email')}
                    disabled={busy}
                    css={css`
                        flex: 1 1 auto;
                    `}
                />
                <CompactField
                    variant="outlined"
                    placeholder="Airport codes · DFW, ONT"
                    value={manualForm.airport_codes}
                    onChange={callbacks.setManualField('airport_codes')}
                    disabled={busy}
                    error={unknownAirportCodes.length > 0}
                    helperText={
                        unknownAirportCodes.length > 0 ? `Unknown: ${unknownAirportCodes.join(', ')}` : undefined
                    }
                    css={css`
                        flex: 0 0 200px;
                    `}
                />
            </FieldRow>

            <FieldRow>
                <CompactField
                    select
                    variant="outlined"
                    value={manualForm.leg}
                    onChange={callbacks.setManualField('leg')}
                    disabled={busy}
                    css={css`
                        flex: 1 1 auto;
                    `}
                >
                    {LEG_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </CompactField>
                <PrimaryButton
                    onClick={callbacks.sendInvite}
                    disabled={!manualValid || busy}
                    loading={busy}
                    css={css`
                        flex: 0 0 auto;
                        min-width: 0;
                        white-space: nowrap;
                    `}
                >
                    <span
                        css={css`
                            display: inline-flex;
                            align-items: center;
                            gap: 6px;
                        `}
                    >
                        <MailOutline
                            css={css`
                                font-size: 14px;
                            `}
                        />
                        Send invite
                    </span>
                </PrimaryButton>
            </FieldRow>
        </Section>
    );
};

/**
 * Right-hand intake panel for adding carriers to a corporate account, opened from the
 * Flex Centers tab's action button.
 *
 * Two ways in, one path through: both a CSV upload and a single typed invite are matched
 * against existing accounts by DOT first, and any DOT already on Onward (or repeated in
 * the file) stops for a decision in UploadExceptionsModal — connect the existing
 * account, skip, or add a separate one — so nothing is duplicated silently.
 */
const AddCarriersDrawer = ({ open, onClose }) => {
    const { state, callbacks } = useAddCarriers({ open, onClose });

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={callbacks.close}
                PaperProps={{ style: { width: DRAWER_WIDTH, maxWidth: '100vw' } }}
            >
                {state.busy ? (
                    <LinearProgress
                        color="primary"
                        css={css`
                            width: 100%;
                        `}
                    />
                ) : null}
                <Panel>
                    <HeaderRow>
                        <div
                            css={css`
                                min-width: 0;
                            `}
                        >
                            <Title>Add Carriers</Title>
                            <Subtitle>Final mile · LTL · Truckload · Flex locations · Motor carriers</Subtitle>
                        </div>
                        <IconButton
                            size="small"
                            onClick={callbacks.close}
                            disabled={state.committing}
                            aria-label="Close add carriers"
                            css={css`
                                margin: -6px -6px 0 0;
                            `}
                        >
                            <Close />
                        </IconButton>
                    </HeaderRow>

                    <Divider />

                    {!state.organization_id ? (
                        <InfoBanner
                            css={css`
                                color: ${colors.reds.primary};
                            `}
                        >
                            Your account is not associated with an organization — contact support before adding
                            carriers.
                        </InfoBanner>
                    ) : null}

                    <CsvUploadSection state={state} callbacks={callbacks} />

                    <Divider />

                    <InviteOneSection state={state} callbacks={callbacks} />

                    <InfoBanner>
                        {DEDUPE_NOTE_LEAD}
                        <Emphasis>{DEDUPE_NOTE_EMPHASIS}</Emphasis>
                        {DEDUPE_NOTE_TAIL}
                    </InfoBanner>
                </Panel>
            </Drawer>

            <UploadExceptionsModal state={state} callbacks={callbacks} />
        </>
    );
};

export default AddCarriersDrawer;
